import { useState, useMemo } from "react";

export interface UsePaginationProps<T> {
    data: T[];
    itemsPerPage?: number;
    initialPage?: number;
}

export interface UsePaginationReturn<T> {
    paginatedData: T[];

    totalItems: number;
    itemsPerPage: number;
    startIndex: number;
    endIndex: number;

    goToNextPage: () => void;
    goToPreviousPage: () => void;
    goToFirstPage: () => void;
    goToLastPage: () => void;
    canGoToNextPage: boolean;
    canGoToPreviousPage: boolean;
    onItemsPerPageChange: (itemsPerPage: number) => void;
}

export const usePagination = <T>({
    data,
    itemsPerPage = 10,
    initialPage = 1,
}: UsePaginationProps<T>): UsePaginationReturn<T> => {
    const [currentItemsPerPage, setCurrentItemsPerPage] = useState(itemsPerPage);
    const [currentPage, setCurrentPage] = useState(initialPage);

    const totalItems = data.length;
    const totalPages = Math.ceil(totalItems / currentItemsPerPage);

    const paginatedData = useMemo(() => {
        const startIndex = (currentPage - 1) * currentItemsPerPage;
        const endIndex = startIndex + currentItemsPerPage;

        return data.slice(startIndex, endIndex);
    }, [data, currentPage, currentItemsPerPage]);

    const goToPage = (page: number) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

    const goToNextPage = () => goToPage(currentPage + 1);

    const goToPreviousPage = () => goToPage(currentPage - 1);

    const goToFirstPage = () => goToPage(1);

    const goToLastPage = () => goToPage(totalPages);

    const canGoToNextPage = currentPage < totalPages;
    const canGoToPreviousPage = currentPage > 1;

    const startIndex = (currentPage - 1) * currentItemsPerPage + 1;
    const endIndex = Math.min(currentPage * currentItemsPerPage, totalItems);

    const handleItemsPerPageChange = (newItemsPerPage: number) => {
        setCurrentItemsPerPage(newItemsPerPage);
        goToFirstPage();
    };

    return {
        paginatedData,

        totalItems,
        itemsPerPage: currentItemsPerPage,
        startIndex,
        endIndex,

        goToNextPage,
        goToPreviousPage,
        goToFirstPage,
        goToLastPage,
        canGoToNextPage,
        canGoToPreviousPage,
        onItemsPerPageChange: handleItemsPerPageChange,
    };
};
